import { FC, useMemo } from "react";
import ReactFlow, {
  Background,
  Node,
  Edge,
  ReactFlowProvider,
} from "reactflow";
import { Box } from "@radix-ui/themes";
import { Workflow, WorkflowStep } from "@/types/breeze/Workflow";
import { StepNode } from "./editor/StepNode";
import { StartNode } from "./editor/StartNode";
import "reactflow/dist/style.css";

interface WorkflowPreviewProps {
  workflow: Workflow;
  width?: number;
  height?: number;
}

// Use the actual editor node types but scaled down for preview
const previewNodeTypes = {
  stepNode: (props: {
    data: {
      step: WorkflowStep;
      onEditClicked: () => void;
      onDeleteClicked: () => void;
      isSelected: boolean;
    };
  }) => (
    <div style={{ transform: "scale(0.5)", transformOrigin: "center" }}>
      <StepNode {...props} />
    </div>
  ),
  startNode: (props: { data: object }) => (
    <div style={{ transform: "scale(0.5)", transformOrigin: "center" }}>
      <StartNode {...props} />
    </div>
  ),
};

const WorkflowPreviewContent: FC<WorkflowPreviewProps> = ({
  workflow,
  width = 200,
  height = 150,
}) => {
  const { nodes, edges } = useMemo(() => {
    const nodes: Node[] = [];
    const edges: Edge[] = [];

    if (workflow.steps.length === 0) {
      // Just show start node if no steps
      nodes.push({
        id: "start",
        type: "startNode",
        position: { x: width / 2 - 30, y: height / 2 - 30 },
        data: {},
      });
      return { nodes, edges };
    }

    // Account for scaled node sizes (120px start node, 256px step nodes scaled to 50%)
    const startNodeWidth = 120 * 0.5; // 60px
    const stepNodeWidth = 256 * 0.5; // 128px
    const nodeSpacing = 60;

    // Create start node (no-op handlers for preview)
    const noOpHandlers = {
      onEditClicked: () => {},
      onDeleteClicked: () => {},
      isSelected: false,
    };

    nodes.push({
      id: "start",
      type: "startNode",
      position: { x: 10, y: height / 2 - 30 },
      data: {},
    });

    // Find start step and create a linear flow
    const startStep = workflow.steps.find((s) => s.is_start_step);
    const orderedSteps: WorkflowStep[] = [];

    if (startStep) {
      // Build a simple linear chain from start step
      let currentStep: WorkflowStep | undefined = startStep;
      const visited = new Set<number>();

      while (currentStep && !visited.has(currentStep.id)) {
        visited.add(currentStep.id);
        orderedSteps.push(currentStep);

        // Find next step via success path
        const nextStepId: number | null = currentStep.on_success_step_id;
        currentStep = nextStepId
          ? workflow.steps.find((s) => s.id === nextStepId)
          : undefined;
      }
    }

    // If no clear chain, just use all steps
    if (orderedSteps.length === 0) {
      orderedSteps.push(...workflow.steps);
    }

    // Position step nodes
    orderedSteps.forEach((step, index) => {
      nodes.push({
        id: step.key,
        type: "stepNode",
        position: {
          x: 10 + startNodeWidth + nodeSpacing + index * (stepNodeWidth + 20),
          y: height / 2 - 60,
        },
        data: {
          step,
          ...noOpHandlers,
        },
      });
    });

    // Create edges
    if (orderedSteps.length > 0) {
      // Start to first step
      edges.push({
        id: "start-edge",
        source: "start",
        target: orderedSteps[0].key,
        type: "smoothstep",
        style: { stroke: "var(--accent-9)", strokeWidth: 2 },
      });

      // Connect steps in sequence
      for (let i = 0; i < orderedSteps.length - 1; i++) {
        edges.push({
          id: `${orderedSteps[i].key}-${orderedSteps[i + 1].key}`,
          source: orderedSteps[i].key,
          target: orderedSteps[i + 1].key,
          type: "smoothstep",
          style: { stroke: "var(--accent-9)", strokeWidth: 2 },
        });
      }
    }

    return { nodes, edges };
  }, [workflow, width, height]);

  return (
    <Box
      style={{
        width: `${width}px`,
        height: `${height}px`,
        border: "1px solid var(--gray-6)",
        borderRadius: "var(--radius-2)",
        overflow: "hidden",
        background: "var(--gray-1)",
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={previewNodeTypes}
        fitView
        attributionPosition="bottom-right"
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={false}
        zoomOnScroll={false}
        zoomOnPinch={false}
        zoomOnDoubleClick={false}
        panOnScroll={false}
        panOnDrag={false}
        proOptions={{ hideAttribution: true }}
        style={{ background: "transparent" }}
      >
        <Background color="var(--gray-5)" size={0.5} />
      </ReactFlow>
    </Box>
  );
};

export const WorkflowPreview: FC<WorkflowPreviewProps> = (props) => {
  return (
    <ReactFlowProvider>
      <WorkflowPreviewContent {...props} />
    </ReactFlowProvider>
  );
};
